import { AthletePreview } from '@strength/domain';
import { DateTime } from 'luxon';
import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from '@tanstack/react-router';
import { trackEvent } from '../../../reporting/reporting';
import { trpc } from '../../../trpc/react';
import { DateRange } from '../../components/dates/RangeCalendar';
import { Page } from '../../components/layout/page';
import { PageHeader } from '../../components/layout/page-header';
import { TeamSelect } from '../../components/layout/TeamSelect';
import { Tabs, TabTrigger } from '../../components/tab/tab';
import { NotesTable } from './components/NotesTable';
import { NotesSheet } from './NotesSheet';
import { Note } from './types';

type NoteType = 'Inbox' | 'Archived';

function useFilterState<T>(): [
  T[],
  Dispatch<SetStateAction<T[]>>,
  T[],
  Dispatch<SetStateAction<T[]>>,
] {
  const [options, setOptions] = useState<T[]>([]);
  const [selected, setSelected] = useState<T[]>([]);

  return [options, setOptions, selected, setSelected];
}

export function NotesPage() {
  const { t } = useTranslation();
  const teamId = useParams({
    from: '/orgAuth/orgWithNav/$teamId/notes',
    select: (params: { teamId: string }) => params.teamId,
  });
  const [type, setType] = useState<NoteType>('Inbox');
  const [noteToDisplay, setNoteToDisplay] = useState<Note | null>(null);
  const [athletes, setAthletes, selectedAthletes, setSelectedAthletes] =
    useFilterState<AthletePreview>();

  const [dateRange, setDateRange] = useState<DateRange | null>({
    start: DateTime.now().minus({ month: 1 }).toISODate(),
    end: DateTime.now().toISODate(),
  });

  // Alert queries
  const alertsQuery = trpc.alert.alerts.useQuery({
    athleteIds:
      selectedAthletes.map((a) => a.id).length === athletes.length
        ? []
        : selectedAthletes.map((a) => a.id),
    startDate: dateRange?.start,
    endDate: dateRange?.end,
    resolved: type === 'Archived',
  });

  const markResolvedQuery = trpc.alert.markResolved.useMutation({
    onSuccess: () => {
      alertsQuery.refetch();
    },
  });

  // Filter queries
  trpc.roster.teamAthletes.useQuery({}, {
      onSuccess: (data) => {
        setAthletes(data);
        setSelectedAthletes(data);
      },
    },
  );

  const handleFilterChange = (filters: Record<string, any>) => {
    const athleteFilter = filters['athletes'];
    setSelectedAthletes(athleteFilter ? athleteFilter : [...athletes]);
    const dateRangeFilter = filters['dateRange'];
    setDateRange(
      dateRangeFilter
        ? {
            start: dateRangeFilter['value']['start'],
            end: dateRangeFilter['value']['end'],
          }
        : null,
    );
  };

  const openDrawer = (note: Note) => {
    setNoteToDisplay(note);
    trackEvent('Strength | Notes | Slideout Opened', {
      noteId: note.alertId,
    });
  };

  const markAsResolved = (noteId: string, resolved: boolean) => {
    markResolvedQuery.mutate([{ id: noteId, resolved }]);
    setNoteToDisplay(null);
    alertsQuery.refetch();

    trackEvent('Strength | Notes | Note Archived', {
      noteId,
    });
  };

  return (
    <Page>
      <PageHeader
        title={t('notes')}
        rightSlot={
          <TeamSelect sx={{ mt: 0 }} teamId={teamId} />
        }
      />
      <div className="flex flex-grow justify-between items-center p-6">
        <Tabs
          sx={{
            alignSelf: 'flex-end',
            width: 400,
          }}
          value={type}
          onChange={(_, newValue) => {
            alertsQuery.refetch();
            setType(newValue);
          }}
          aria-label="Inbox and Archived tabs"
        >
          <TabTrigger label={t('inbox')} value="Inbox" sx={{ width: 100 }} />
          <TabTrigger
            label={t('archived')}
            value="Archived"
            sx={{ width: 100 }}
          />
        </Tabs>
      </div>
      <div
        role="tabpanel"
        id={`tabpanel-${type}`}
        aria-labelledby={`tab-${type}`}
        className="grow flex h-full px-6 pb-6 overflow-hidden"
      >
        <NotesTable
          notes={alertsQuery.data || []}
          loading={alertsQuery.isLoading}
          type={type}
          athletes={athletes}
          onRowClick={openDrawer}
          onFilterChange={handleFilterChange}
          onMarkAsResolved={markAsResolved}
        />
      </div>

      {noteToDisplay && (
        <NotesSheet
          onSave={(id, resolved) => {
            markResolvedQuery.mutate([{ id, resolved }]);
            setNoteToDisplay(null);
          }}
          open={!!noteToDisplay}
          note={noteToDisplay}
          onClose={() => {
            setNoteToDisplay(null);
          }}
        />
      )}
    </Page>
  );
}
